/**
 * Email Branding Settings Tab
 *
 * Lets admins customize their agency's outbound transactional emails
 * (currently used for ROI signature requests) and the public ROI signing
 * page header. Provides a live email preview pane.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Image as ImageIcon, Save, Loader2, Upload, Trash2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  logo_data_url: "",
  accent_color: "#0f172a",
  from_name: "",
  from_email: "",
  reply_to: "",
  welcome_line: "",
  signature_block: "",
  custom_domain: "",
};

const PRESET_COLORS = [
  "#0f172a", // slate
  "#1e3a8a", // navy
  "#0d9488", // teal
  "#16a34a", // green
  "#7c3aed", // violet
  "#db2777", // pink
  "#c2410c", // burnt orange
  "#b45309", // amber
];

export const BrandingSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(DEFAULTS);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/branding`);
      setBranding({ ...DEFAULTS, ...res.data });
    } catch (e) {
      toast.error("Failed to load branding settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG or JPG)");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Logo must be under 500KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setBranding((prev) => ({ ...prev, logo_data_url: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setBranding((prev) => ({ ...prev, logo_data_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/branding`, branding);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {/* Editor */}
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Email Branding
          </CardTitle>
          <CardDescription>
            Customize the look and tone of outbound emails (e.g., Release of
            Information signature requests) and the public ROI signing page header.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div className="space-y-2">
            <Label>Agency Logo</Label>
            <div className="flex items-start gap-4">
              <div className="w-32 h-20 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden">
                {branding.logo_data_url ? (
                  <img
                    src={branding.logo_data_url}
                    alt="Agency logo preview"
                    className="max-w-full max-h-full object-contain"
                    data-testid="branding-logo-preview"
                  />
                ) : (
                  <ImageIcon className="w-7 h-7 text-slate-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                  data-testid="branding-logo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="branding-logo-upload-btn"
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload Logo
                </Button>
                {branding.logo_data_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    className="gap-2 text-red-600 hover:text-red-700"
                    data-testid="branding-logo-remove-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </Button>
                )}
                <p className="text-xs text-slate-500">PNG/JPG/SVG, under 500KB</p>
              </div>
            </div>
          </div>

          {/* Accent Color */}
          <div className="space-y-2">
            <Label>Accent Color</Label>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="color"
                value={branding.accent_color || "#0f172a"}
                onChange={(e) =>
                  setBranding((p) => ({ ...p, accent_color: e.target.value }))
                }
                className="w-10 h-10 rounded cursor-pointer border"
                data-testid="branding-color-input"
              />
              <Input
                value={branding.accent_color || ""}
                onChange={(e) =>
                  setBranding((p) => ({ ...p, accent_color: e.target.value }))
                }
                className="w-32 font-mono text-xs"
                placeholder="#0f172a"
              />
              <div className="flex gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setBranding((p) => ({ ...p, accent_color: c }))
                    }
                    className="w-7 h-7 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                    style={{ background: c }}
                    aria-label={`Use color ${c}`}
                    data-testid={`branding-color-preset-${c.slice(1)}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Used for buttons and links in your branded emails and the signing page.
            </p>
          </div>

          {/* From-name + reply-to */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>From Name</Label>
              <Input
                value={branding.from_name || ""}
                onChange={(e) =>
                  setBranding((p) => ({ ...p, from_name: e.target.value }))
                }
                placeholder="e.g., Prestige Home Care"
                data-testid="branding-from-name-input"
              />
              <p className="text-xs text-slate-500">
                Shown in the recipient's inbox. Defaults to your agency name.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Sender Email Address</Label>
              <Input
                type="email"
                value={branding.from_email || ""}
                onChange={(e) =>
                  setBranding((p) => ({ ...p, from_email: e.target.value }))
                }
                placeholder={
                  branding.custom_domain
                    ? `noreply@${(branding.custom_domain || "").replace(/^https?:\/\//, "")}`
                    : "noreply@yourdomain.com"
                }
                data-testid="branding-from-email-input"
              />
              <p className="text-xs text-slate-500">
                Optional. Requires your domain to be{" "}
                <a
                  href="https://resend.com/domains"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-slate-700"
                >
                  verified in Resend
                </a>
                . If blank, we use a shared sender.
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Reply-To Email</Label>
              <Input
                type="email"
                value={branding.reply_to || ""}
                onChange={(e) =>
                  setBranding((p) => ({ ...p, reply_to: e.target.value }))
                }
                placeholder="support@yourdomain.com"
                data-testid="branding-reply-to-input"
              />
              <p className="text-xs text-slate-500">
                Where replies are routed if a client hits Reply. Falls back to
                your sender address, then your agency contact email.
              </p>
            </div>
          </div>

          {/* Welcome line */}
          <div className="space-y-1">
            <Label>Welcome Line (optional)</Label>
            <Textarea
              rows={2}
              value={branding.welcome_line || ""}
              onChange={(e) =>
                setBranding((p) => ({ ...p, welcome_line: e.target.value }))
              }
              placeholder="Welcome to Prestige Home Care! We're so glad you chose us for your care needs."
              data-testid="branding-welcome-line-input"
            />
          </div>

          {/* Signature block */}
          <div className="space-y-1">
            <Label>Signature / Closing (optional)</Label>
            <Textarea
              rows={3}
              value={branding.signature_block || ""}
              onChange={(e) =>
                setBranding((p) => ({ ...p, signature_block: e.target.value }))
              }
              placeholder={"— The Prestige Care Team\n555-123-4567 | care@prestige.com"}
              data-testid="branding-signature-block-input"
            />
          </div>

          {/* Custom domain — used for public signature links (ROI etc.) */}
          <div className="space-y-1">
            <Label>Public Signing URL (Custom Domain)</Label>
            <Input
              value={branding.custom_domain || ""}
              onChange={(e) =>
                setBranding((p) => ({ ...p, custom_domain: e.target.value }))
              }
              placeholder="https://carepilotusa.com"
              data-testid="branding-custom-domain-input"
            />
            <p className="text-xs text-slate-500">
              The domain your clients will see in ROI signature links (e.g.,{" "}
              <code>https://carepilotusa.com</code>). Leave blank to auto-detect.
              You can omit <code>https://</code> — we'll add it for you.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
              data-testid="branding-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Branding
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live Preview */}
      <div className="lg:col-span-2 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Email Preview</h3>
        <BrandedEmailPreview branding={branding} />
        <p className="text-xs text-slate-500">
          This is approximately how recipients will see your ROI signature request.
        </p>
      </div>
    </div>
  );
};

function BrandedEmailPreview({ branding }) {
  const accent = branding.accent_color || "#0f172a";
  return (
    <div
      className="border rounded-xl bg-slate-100 p-3"
      data-testid="branding-email-preview"
    >
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-5 pt-5 pb-2">
          {branding.logo_data_url ? (
            <img
              src={branding.logo_data_url}
              alt="Logo preview"
              style={{ maxHeight: 40, maxWidth: 180 }}
            />
          ) : null}
          <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-3">
            {branding.from_name || "Your Agency"}
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mt-1">
            Please Sign: Release of Information
          </h2>
          {branding.welcome_line && (
            <p className="text-sm text-slate-700 mt-2 leading-snug">
              {branding.welcome_line}
            </p>
          )}
          <p className="text-sm text-slate-600 mt-2 leading-snug">
            Hello there, {branding.from_name || "your agency"} has requested
            your authorization to release health information. Please review and
            sign the form below electronically.
          </p>
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            style={{ background: accent }}
            className="text-white text-sm font-semibold px-4 py-2 rounded-md"
          >
            Review &amp; Sign
          </button>
          {branding.signature_block && (
            <p className="text-xs text-slate-600 mt-4 whitespace-pre-line">
              {branding.signature_block}
            </p>
          )}
        </div>
        <div className="bg-slate-50 border-t px-5 py-3 text-[10px] text-slate-500 leading-snug">
          Secure electronic signature request. If unexpected, please ignore.
        </div>
      </div>
    </div>
  );
}

export default BrandingSettingsTab;
